<template>
  <el-popover
    v-model:visible="popoverVisible"
    placement="bottom-end"
    :width="380"
    trigger="click"
    :teleported="false"
    @show="onPopoverShow"
  >
    <template #reference>
      <div class="notification-bell" :title="t('dashboard.notification.title')">
        <el-badge :value="totalUnread" :hidden="totalUnread === 0" :max="99">
          <Bell :size="18" />
        </el-badge>
      </div>
    </template>

    <!-- 通知面板 -->
    <div class="notification-panel">
      <div class="panel-header">
        <span class="panel-title">{{ t('dashboard.notification.title') }}</span>
        <el-button v-if="totalUnread > 0" type="primary" link size="small" @click="handleMarkAllRead">
          {{ t('dashboard.notification.markAllRead') }}
        </el-button>
      </div>

      <!-- 类型tab -->
      <div class="type-tabs">
        <span v-for="tab in typeTabs" :key="tab.key"
              :class="['type-tab', { active: activeType === tab.key }]"
              @click="switchType(tab.key)">
          {{ t(tab.label) }}
          <em v-if="getTypeCount(tab.key) > 0">{{ getTypeCount(tab.key) }}</em>
        </span>
      </div>

      <!-- 通知列表 -->
      <div v-loading="loading" class="notification-list">
        <div v-if="list.length === 0" class="empty-state">
          <Bell :size="28" style="color: var(--text-placeholder);" />
          <p>{{ t('dashboard.notification.noNotification') }}</p>
        </div>

        <div v-for="item in list" :key="item.id"
             :class="['notification-item', { unread: item.readStatus === 0 }]"
             @click="handleItemClick(item)">
          <div class="item-dot" v-if="item.readStatus === 0" />
          <div class="item-body">
            <div class="item-title">{{ item.title }}</div>
            <div class="item-content" v-if="item.content">{{ item.content }}</div>
            <div class="item-time">{{ formatTime(item.createTime) }}</div>
          </div>
        </div>
      </div>
    </div>
  </el-popover>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useDashboardStore } from '@/stores/dashboard'
import { getNotificationList, markAsRead, markAllAsRead } from '@/api/notification'
import { NOTIFICATION_TYPES } from '@/config/workbenchConfig'
import { Bell } from 'lucide-vue-next'

const { t } = useI18n()
const router = useRouter()
const dashboardStore = useDashboardStore()

const activeType = ref('all')
const list = ref([])
const loading = ref(false)
const popoverVisible = ref(false)

// 订阅 SSE 通知推送：dashboardStore.notificationVersion 在每次收到 NOTIFICATION 时 +1。
// 若铃铛面板正打开，立即静默刷新列表（不显示 loading 遮罩，避免闪屏）。
watch(
  () => dashboardStore.notificationVersion,
  () => {
    if (popoverVisible.value) {
      loadList(true)
    }
  }
)

const totalUnread = computed(() => dashboardStore.totalUnread)
const typeTabs = computed(() => NOTIFICATION_TYPES)

function getTypeCount(key) {
  if (key === 'all') return dashboardStore.unreadCount.total || 0
  return dashboardStore.unreadCount[key] || 0
}

async function loadList(silent = false) {
  if (!silent) loading.value = true
  try {
    const params = { page: 1, size: 20 }
    if (activeType.value !== 'all') {
      params.type = activeType.value
    }
    const res = await getNotificationList(params)
    list.value = res.data?.records || []
  } catch (e) {
    console.error('加载通知列表失败:', e)
  } finally {
    if (!silent) loading.value = false
  }
}

function onPopoverShow() {
  loadList()
}

function switchType(type) {
  activeType.value = type
  loadList()
}

async function handleItemClick(item) {
  if (item.readStatus === 0) {
    try {
      await markAsRead(item.id)
      item.readStatus = 1
      dashboardStore.loadUnreadCount()
    } catch (e) {
      // ignore
    }
  }
  if (item.link) {
    let targetLink = item.link

    // 统一跳转：工作流通知使用 /{业务}/detail/{id} 稳定路径，
    // 由 router/index.js 中的静态 redirect 路由映射到实际动态路由。
    // 不再对任何业务做特殊路径重写。

    // 截取路径部分（去掉查询参数）用于路由匹配检查
    const pathOnly = targetLink.split('?')[0]
    // 静态路由白名单 - 这些路由在 router/index.js 中有静态配置（redirect），不需要权限检查
    const staticRoutePrefixes = [
      '/contract/detail/',       // 租赁合同
      '/joint-contract/detail/', // 联营合同
      '/bill/detail/',           // 账单
      '/settlement/detail/',     // 联营结算
      '/purchase-contract/detail/', // 采购合同
      '/intention/detail/',      // 意向
      '/renovation/detail/',     // 装修
      '/termination/detail/',    // 退出
      '/merchant/policy/',       // 商户政策
      '/redirect/',              // 重定向
      '/contract/amend/',        // 合同变更
      '/system/printTemplate/designer' // 打印模板设计器
    ]
    const isStaticRoute = staticRoutePrefixes.some(prefix => pathOnly.startsWith(prefix))
    if (isStaticRoute) {
      router.push(targetLink)
      return
    }
    const resolved = router.resolve(pathOnly)
    // 排除 catch-all 路由，确认是真实注册的路由
    const isRealRoute = resolved.matched.some(r => r.path !== '/:pathMatch(.*)*')
    if (!isRealRoute) {
      ElMessage.warning('暂无该页面的访问权限')
      return
    }
    router.push(targetLink)
  }
}

async function handleMarkAllRead() {
  try {
    await markAllAsRead()
    list.value.forEach(item => { item.readStatus = 1 })
    dashboardStore.loadUnreadCount()
  } catch (e) {
    console.error('标记全部已读失败:', e)
  }
}

function formatTime(time) {
  if (!time) return ''
  const d = new Date(time)
  const now = new Date()
  const diff = now - d
  if (diff < 60000) return '刚刚'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
  return `${d.getMonth() + 1}-${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
</script>

<style lang="scss" scoped>
.notification-bell {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: background 0.2s;
  color: var(--text-regular);

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: var(--primary);
  }
}

.notification-panel {
  margin: -12px;

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-light);

    .panel-title {
      font-size: 15px;
      font-weight: 600;
      color: var(--text-primary);
    }
  }

  .type-tabs {
    display: flex;
    gap: 4px;
    padding: 8px 16px;
    border-bottom: 1px solid var(--border-light);

    .type-tab {
      padding: 3px 10px;
      font-size: 12px;
      color: var(--text-secondary);
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover, &.active {
        color: var(--primary);
        background: var(--bg-info-light);
      }

      em {
        font-style: normal;
        font-size: 10px;
        background: var(--danger);
        color: white;
        padding: 0 4px;
        border-radius: 6px;
        margin-left: 3px;
      }
    }
  }

  .notification-list {
    max-height: 360px;
    overflow-y: auto;
    min-height: 100px;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 0;
    color: var(--text-placeholder);

    p {
      margin-top: 8px;
      font-size: 13px;
    }
  }

  .notification-item {
    display: flex;
    align-items: flex-start;
    padding: 10px 16px;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: var(--bg-page);
    }

    &.unread {
      background: var(--bg-info-light);

      .item-title {
        font-weight: 600;
      }
    }

    .item-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--primary);
      margin-top: 6px;
      margin-right: 8px;
      flex-shrink: 0;
    }

    .item-body {
      flex: 1;
      min-width: 0;

      .item-title {
        font-size: 13px;
        color: var(--text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .item-content {
        font-size: 12px;
        color: var(--text-secondary);
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .item-time {
        font-size: 11px;
        color: var(--text-placeholder);
        margin-top: 4px;
      }
    }
  }
}

// el-popover 深色模式适配（需非 scoped 覆盖 Element Plus 样式）
:deep(.el-popover.el-popper) {
  background: var(--bg-card);
  border-color: var(--border-base);
  color: var(--text-regular);
}
</style>
